import React, { useState } from "react";
import { IoCameraOutline } from "react-icons/io5";
import { MdClose } from "react-icons/md";
import { useFormik } from "formik";
import * as Yup from "yup";
import { postFormDataApiCaller } from "../../../../../../../Libs/apiCaller";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { converId } from "../../../../../../../Redux/slices/ChatSlice";
import { decryptText } from "../../../../../../../Components/EncryptDecryptMessage/EncryptDecryptMessage";

export default function NewGroupSection({
  handleCreateGroupDone,
  selectedChats,
  setSelectedChats,
  setCreateNewGroupSection,
  setGroupActiveChat,
  setChatConversationId,
  setGetSecretKey,
}) {
  const { userInfo } = useSelector((state) => state.authUser);
  const { socket } = useSelector((state) => state.socket);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const dispatch = useDispatch();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024); // Convert to MB
      if (fileSizeInMB > 5) {
        setImageError("Image size should be less than 5MB.");
      } else {
        setImage(file);
        setImagePreview(URL.createObjectURL(file));
        setImageError("");
      }
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    // setImageError("Image is required");
  };

  const formik = useFormik({
    initialValues: {
      groupName: "",
      description: "",
    },
    validationSchema: Yup.object({
      groupName: Yup.string()
        .required("Group Name is required")
        .test("no-blank", "Group Name cannot be empty", (value) =>
          value?.trim() ? true : false
        )
        .min(3, "Group Name must be at least 3 characters"),
    }),
    onSubmit: async (values) => {
      // if (!image) {
      //   setImageError("Image is required");
      //   return;
      // }
      // setImageError(""); // clear error if image exists
      setIsSubmitLoading(true);
      const idString = getIdStringFromArray(selectedChats);
      const formData = new FormData();
      formData.append("groupName", values.groupName);
      formData.append("description", values.description);
      formData.append("users", idString);
      image && formData.append("messageMedia", image);

      const endPoint = "group/create";
      const response = await postFormDataApiCaller(endPoint, formData);
      if (response?.status === 200 || response?.status === 201) {
        getSecretKeyFunction(
          response?.data?.data?.conversationId?.AESKeyOfUser
        ).then(() => {
          dispatch(converId(response.data.data?.conversationId?._id));
          setChatConversationId(response?.data?.data.conversationId);
          setChatConversationId((prev) => {
            if (prev?._id) {
              handleChatChangeConversation(prev._id);
            }
            joinConversation(response.data.data?.conversationId?._id);
            return response.data.data?.conversationId;
          });
          setImage(null);
          setImagePreview(null);
          setSelectedChats([]);
          setCreateNewGroupSection(0);
          formik.resetForm();
          setGroupActiveChat(response.data.data);
          handleCreateGroupDone();
        });
      } else {
        toast.error(response?.response?.data?.message || response?.message);
      }
      setIsSubmitLoading(false);
    },
  });

  const joinConversation = (conversationId) => {
    socket.emit("joinConversation", { id: conversationId });
  };

  const handleChatChangeConversation = async (conversationId) => {
    socket.emit("disconnectConversation", { id: conversationId });
  };

  const getSecretKeyFunction = async (AESKeyOfUser) => {
    const getKey = await decryptText(
      AESKeyOfUser,
      userInfo?.encryptedPrivateKey
    );
    setGetSecretKey(getKey);
  };

  const getIdStringFromArray = (data) => {
    return data.map((item) => item._id).join(",");
  };

  return (
    <div className="bg-white">
      <form
        onSubmit={formik.handleSubmit}
        className="max-w-md mx-auto bg-white"
      >
        <div className="my-4 w-[130px] h-[130px] mx-auto rounded-4xl border border-dashed border-[#000] overflow-hidden relative">
          <label
            htmlFor="imageUpload"
            className="cursor-pointer w-full h-full flex items-center justify-center"
          >
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Group"
                className="w-full h-full object-cover rounded-4xl"
              />
            ) : (
              <IoCameraOutline className="text-[#7104F8] text-3xl" />
            )}
          </label>

          {imagePreview && (
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-1 right-1 bg-[var(--primary-color)] rounded-full p-1 shadow"
            >
              <MdClose className="text-white text-xl" />
            </button>
          )}
          <input
            type="file"
            id="imageUpload"
            accept=".jpg,.png"
            onChange={handleImageChange}
            className="hidden"
            value=""
          />
        </div>
        {imageError && (
          <p className="text-red-500 text-center text-xs mt-1">{imageError}</p>
        )}

        <div className="mb-4">
          <label className="block text-sm text-gray-500 font-normal mb-1">
            Group Name
          </label>
          <input
            type="text"
            name="groupName"
            placeholder="Enter Group Name"
            value={formik.values.groupName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            maxLength={30}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {formik.touched.groupName && formik.errors.groupName && (
            <p className="text-red-500 text-xs mt-1">
              {formik.errors.groupName}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-gray-500 text-sm font-normal mb-1">
            Description
          </label>
          <textarea
            name="description"
            placeholder="Enter Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {formik.touched.description && formik.errors.description && (
            <p className="text-red-500 text-xs mt-0">
              {formik.errors.description}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="btn bg-[var(--primary-color)] text-white w-full h-12 font-normal rounded-full border-0"
          disabled={isSubmitLoading}
        >
          {isSubmitLoading ? "Loading..." : "Done"}
        </button>
      </form>
    </div>
  );
}
